import { useEffect, useState, type FormEvent } from 'react'
import { Loader2 } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import type { Firm } from '@toga/shared'
import { useAuth } from '../../contexts/AuthContext'

export default function FirmPage() {
  const auth = useAuth()
  const canEdit = auth.status === 'authenticated' && auth.user.role === 'ADMIN'

  const [firm, setFirm] = useState<Firm | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        const { firm } = await api.get<{ firm: Firm }>('/firm')
        setFirm(firm)
      } catch (err) {
        setMessage({ kind: 'error', text: err instanceof Error ? err.message : 'Error al cargar' })
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  if (loading) {
    return (
      <div className="t-muted flex items-center gap-2">
        <Loader2 size={18} className="animate-spin" /> Cargando…
      </div>
    )
  }
  if (!firm) return <div className="t-muted">Bufete no disponible.</div>

  const update = (patch: Partial<Firm>) => setFirm({ ...firm, ...patch })

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setMessage(null)
    try {
      const { firm: updated } = await api.patch<{ firm: Firm }>('/firm', {
        name: firm.name,
        legalName: firm.legalName,
        cif: firm.cif,
        email: firm.email,
        phone: firm.phone,
        address: firm.address,
        city: firm.city,
        postalCode: firm.postalCode,
        province: firm.province,
        country: firm.country,
        vatRate: firm.vatRate,
        irpfRate: firm.irpfRate,
        invoicePrefix: firm.invoicePrefix,
        invoiceFootnote: firm.invoiceFootnote,
        bankIban: firm.bankIban,
        bankBic: firm.bankBic,
        defaultHourlyRateCents: firm.defaultHourlyRateCents,
        enforceMfaRoles: firm.enforceMfaRoles,
        sessionIdleMinutes: firm.sessionIdleMinutes,
        sessionAbsoluteHours: firm.sessionAbsoluteHours,
      })
      setFirm(updated)
      setMessage({ kind: 'ok', text: 'Datos del bufete actualizados.' })
    } catch (err) {
      const text =
        err instanceof ApiError
          ? ((err.body as { error?: string })?.error ?? err.message)
          : 'Error de red'
      setMessage({ kind: 'error', text })
    } finally {
      setSaving(false)
    }
  }

  const inputCls =
    'w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/60 text-slate-100 focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60 disabled:opacity-60 disabled:cursor-not-allowed'

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-2xl font-serif font-semibold t-primary">Datos del bufete</h2>
        <p className="t-muted text-sm mt-1">
          {canEdit
            ? 'Información de cabecera para facturas, escritos y notificaciones.'
            : 'Solo los administradores pueden modificar estos datos.'}
        </p>
      </header>

      <form onSubmit={onSubmit} className="surface p-6 space-y-6">
        <fieldset disabled={!canEdit || saving} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nombre comercial">
              <input
                required
                value={firm.name}
                onChange={(e) => update({ name: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label="Razón social">
              <input
                value={firm.legalName ?? ''}
                onChange={(e) => update({ legalName: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label="CIF">
              <input
                value={firm.cif ?? ''}
                onChange={(e) => update({ cif: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label="Email">
              <input
                type="email"
                value={firm.email ?? ''}
                onChange={(e) => update({ email: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label="Teléfono">
              <input
                value={firm.phone ?? ''}
                onChange={(e) => update({ phone: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label="País (ISO 3166-1 alfa-2)">
              <input
                value={firm.country}
                onChange={(e) => update({ country: e.target.value.toUpperCase() })}
                maxLength={2}
                className={inputCls}
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Dirección">
              <input
                value={firm.address ?? ''}
                onChange={(e) => update({ address: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label="Ciudad">
              <input
                value={firm.city ?? ''}
                onChange={(e) => update({ city: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label="Código postal">
              <input
                value={firm.postalCode ?? ''}
                onChange={(e) => update({ postalCode: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label="Provincia">
              <input
                value={firm.province ?? ''}
                onChange={(e) => update({ province: e.target.value })}
                className={inputCls}
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-base pt-4">
            <Field label="IVA por defecto (%)">
              <input
                type="number"
                step="0.01"
                min={0}
                max={100}
                value={firm.vatRate}
                onChange={(e) => update({ vatRate: Number(e.target.value) })}
                className={inputCls}
              />
            </Field>
            <Field label="IRPF por defecto (%)">
              <input
                type="number"
                step="0.01"
                min={0}
                max={100}
                value={firm.irpfRate}
                onChange={(e) => update({ irpfRate: Number(e.target.value) })}
                className={inputCls}
              />
            </Field>
            <Field label="Prefijo de factura (legacy)">
              <input
                value={firm.invoicePrefix}
                onChange={(e) => update({ invoicePrefix: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label="Tarifa horaria por defecto (€/h)">
              <input
                type="number"
                step="0.01"
                min={0}
                value={(firm.defaultHourlyRateCents / 100).toFixed(2)}
                onChange={(e) =>
                  update({
                    defaultHourlyRateCents: Math.round(Number(e.target.value || '0') * 100),
                  })
                }
                placeholder="0"
                className={`${inputCls} font-mono`}
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-base pt-4">
            <Field label="IBAN (transferencias)">
              <input
                value={firm.bankIban ?? ''}
                onChange={(e) => update({ bankIban: e.target.value })}
                placeholder="ES00 0000 0000 00 0000000000"
                className={`${inputCls} font-mono text-sm`}
              />
            </Field>
            <Field label="BIC / SWIFT">
              <input
                value={firm.bankBic ?? ''}
                onChange={(e) => update({ bankBic: e.target.value })}
                placeholder="BBVAESMM"
                className={`${inputCls} font-mono text-sm`}
              />
            </Field>
            <div className="md:col-span-2">
              <Field label="Pie legal de las facturas (visible en el PDF)">
                <textarea
                  value={firm.invoiceFootnote ?? ''}
                  onChange={(e) => update({ invoiceFootnote: e.target.value })}
                  rows={3}
                  placeholder="Ej.: datos registrales, régimen de IVA o cuenta para el pago"
                  className={inputCls}
                />
              </Field>
            </div>
          </div>
        </fieldset>

        {/* F15.3 — Políticas de seguridad del bufete */}
        <fieldset disabled={!canEdit} className="space-y-4 border-t border-base pt-5">
          <legend className="text-sm font-semibold t-primary mb-2">Políticas de seguridad</legend>
          <p className="text-xs t-muted -mt-1">
            Los cambios se aplican en el siguiente login de cada usuario. Las sesiones ya abiertas
            se rigen por la política vigente cuando se crearon.
          </p>

          <div>
            <span className="block text-xs t-muted mb-2">
              Exigir verificación en dos pasos (MFA TOTP) a los siguientes roles:
            </span>
            <div className="flex flex-wrap gap-2">
              {(['ADMIN', 'ABOGADO', 'PARALEGAL', 'LECTURA'] as const).map((r) => {
                const checked = firm.enforceMfaRoles.includes(r)
                return (
                  <label
                    key={r}
                    className={[
                      'inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs cursor-pointer transition',
                      checked
                        ? 'bg-toga-500/15 text-toga-200 border-toga-500/40'
                        : 'bg-slate-900/40 t-secondary border-slate-700/50 hover:bg-slate-900/60',
                    ].join(' ')}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...firm.enforceMfaRoles, r]
                          : firm.enforceMfaRoles.filter((x) => x !== r)
                        update({ enforceMfaRoles: next })
                      }}
                      className="sr-only"
                    />
                    {r}
                  </label>
                )
              })}
            </div>
            <p className="text-[11px] t-faint mt-1.5">
              Los usuarios afectados sin MFA serán dirigidos al wizard de configuración tras el
              siguiente login.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Sesión: inactividad máxima (minutos)">
              <input
                type="number"
                min={5}
                max={720}
                value={firm.sessionIdleMinutes}
                onChange={(e) => update({ sessionIdleMinutes: Number(e.target.value || '30') })}
                className={`${inputCls} font-mono`}
              />
            </Field>
            <Field label="Sesión: duración máxima absoluta (horas)">
              <input
                type="number"
                min={1}
                max={168}
                value={firm.sessionAbsoluteHours}
                onChange={(e) => update({ sessionAbsoluteHours: Number(e.target.value || '8') })}
                className={`${inputCls} font-mono`}
              />
            </Field>
          </div>
        </fieldset>

        {message && (
          <div
            className={[
              'text-sm rounded-lg px-3 py-2 border',
              message.kind === 'ok'
                ? 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30'
                : 'text-rose-300 bg-rose-500/10 border-rose-500/30',
            ].join(' ')}
          >
            {message.text}
          </div>
        )}

        {canEdit && (
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga disabled:opacity-50 flex items-center gap-2"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              Guardar
            </button>
          </div>
        )}
      </form>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm t-secondary mb-1.5">{label}</label>
      {children}
    </div>
  )
}
